import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  ContainedButton,
  HeaderOne,
  HeaderThree,
  TextLink,
} from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import ClassCard from 'components/dashboard/ClassCard'
import { k8V2ContentfulMappings } from 'utils/product/contentfulMappings'
import TierLabel from 'components/TierLabel'
import {
  ButtonsContainer,
  StyledAwardIcon,
  StyledButton,
  StyledCreateClassIcon,
  Wrapper,
} from './component.styles'
import { getTier2Slug, getTier2DisplayTitle } from './helpers'
import {
  EXPLORE_PROGRAM_DESCRIPTION,
  EXPLORE_PROGRAM_TITLE,
  MY_CLASSES_TITLE,
  TEACH_DESCRIPTION,
  NO_CLASSES_MESSAGE,
  NO_CLASSES_TRAINING_LINK,
} from './constants'

const ClassList = ({
  classes,
  courseLinks,
  lessonCompletionGoalValue,
  onComponentClick,
  onClassClick,
  program,
  tier = 1,
}) => {
  const extractGradeFromCourseName = rawCourseName => {
    const normalizedCourseName = String(rawCourseName || '')
      .trim()
      .toLowerCase()
    if (!normalizedCourseName) return ''

    const dwbCourseCodeMatch = normalizedCourseName.match(/^(k|\d{1,2})_dwb_/)
    if (dwbCourseCodeMatch?.[1]) {
      return dwbCourseCodeMatch[1]
    }

    if (/^(k|\d{1,2})$/.test(normalizedCourseName)) {
      return normalizedCourseName
    }

    const gradePrefixMatch = normalizedCourseName.match(
      /^grade[\s-]*(k|\d{1,2})/,
    )
    if (gradePrefixMatch?.[1]) {
      return gradePrefixMatch[1]
    }

    return ''
  }

  const resolveCourseSlug = ({ classItem, mappedSlug }) => {
    const normalizedMappedSlug = String(mappedSlug || '')
      .trim()
      .replace(/^\/+|\/+$/g, '')
    if (normalizedMappedSlug) return normalizedMappedSlug

    const rawCourseName = String(classItem?.course_name || '').trim()
    if (!rawCourseName) return ''

    const extractedGrade = extractGradeFromCourseName(rawCourseName)
    if (extractedGrade) {
      return `grade-${extractedGrade}`
    }

    const withoutGradePrefix = rawCourseName.replace(/^grade[\s-]*/i, '')
    return `grade-${withoutGradePrefix}`
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/^\/+|\/+$/g, '')
  }

  return (
    <ul className="class-links">
      {classes.map(cl => {
        // Tier 1 uses courseLinks mapping; Tier 2 uses k8V2ContentfulMappings to get displayTitle/defaultUnit
        const courseTier1 = courseLinks?.find(
          c => cl?.contentfulID === c?.entryId,
        )
        const { displayTitle: tier2DisplayTitle, defaultUnit } =
          k8V2ContentfulMappings?.find(
            course => cl?.contentfulID === course?.id,
          ) || {}

        const gradeDisplayTitle =
          tier === 2 ? tier2DisplayTitle : courseTier1?.title
        return (
          <li key={`class-card-li-${cl?.instance}`}>
            <ClassCard
              className={cl?.title}
              dataTestId={`class-card-${cl?.instance}`}
              defaultUnit={tier === 2 ? defaultUnit : undefined}
              displayTitle={cl?.title}
              goalPercent={tier === 1 ? lessonCompletionGoalValue : undefined}
              gradeDisplayTitle={gradeDisplayTitle}
              includeProgressBar={tier === 1}
              instance={cl?.instance}
              onClick={path => {
                const courseSlug = resolveCourseSlug({
                  classItem: cl,
                  mappedSlug: courseTier1?.slug,
                })

                if (
                  onClassClick &&
                  program === 'digital-well-being' &&
                  tier === 1
                ) {
                  return onClassClick(courseSlug, cl?.instance)()
                }
                if (onComponentClick) {
                  return onComponentClick(path)
                }
                if (onClassClick && tier === 1) {
                  return onClassClick(courseSlug, cl?.instance)()
                }
                return undefined
              }}
              program={program}
              scoID={cl?.scoID}
              tier={tier}
              totalAmount={cl?.totalLessons}
              totalComplete={cl?.completeLessons}
            />
          </li>
        )
      })}
    </ul>
  )
}

ClassList.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoID: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      entryId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func.isRequired,
  program: PropTypes.string,
  tier: PropTypes.number,
}

const PreviewCourseList = ({
  courseLinks,
  onCourseClick,
  onComponentClick,
  tier,
  program,
}) => {
  return (
    <ul className={`course-links${tier ? ` tier-${tier}` : ''}`}>
      {courseLinks &&
        courseLinks.map(c => {
          const handleClick =
            tier === 2 && onComponentClick
              ? () => {
                  const defaultUnitSlug = (
                    k8V2ContentfulMappings?.find(
                      course => course?.id === c?.entryId,
                    ) || {}
                  ).defaultUnit
                  return onComponentClick(
                    `/product/${program}/lessons/tier-2/preview/${getTier2Slug(
                      c,
                    )}/${defaultUnitSlug}`,
                  )
                }
              : onCourseClick(c.slug)
          return (
            <li key={c.slug}>
              <ContainedButton onClick={handleClick}>
                {tier === 2 ? getTier2DisplayTitle(c) : c.title}
              </ContainedButton>
            </li>
          )
        })}
    </ul>
  )
}

PreviewCourseList.propTypes = {
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  program: PropTypes.string,
  tier: PropTypes.number,
}

export const ProductLessons = React.forwardRef(
  (
    {
      classes,
      contentfulCourses,
      courseLinks,
      lessonCompletionGoalValue,
      onClassClick,
      onComponentClick,
      onCourseClick,
      onCreateClass,
      onCreateSmallGroup,
      program,
      title,
    },
    ref,
  ) => {
    const userHasClasses = !!classes && classes.length > 0
    const userDoesNotHaveClasses = !userHasClasses

    // Callback to trigger create class functionality
    const handleCreateClass = useCallback(() => {
      if (onCreateClass) {
        onCreateClass()
      }
    }, [onCreateClass])

    // Callback to trigger create small group functionality
    const handleCreateSmallGroup = useCallback(() => {
      if (onCreateSmallGroup) {
        onCreateSmallGroup()
        return
      }
      if (onCreateClass) {
        onCreateClass()
      }
    }, [onCreateSmallGroup, onCreateClass])

    return (
      <Wrapper>
        <header>
          <HeaderOne>{title}</HeaderOne>
        </header>

        <section>
          <header>
            <div className="header-text">
              <div className="headline">
                <HeaderThree>{MY_CLASSES_TITLE}</HeaderThree>
                <TierLabel labelText="TIER 1" />
                <div className="spacer" />
              </div>
              <p>{TEACH_DESCRIPTION}</p>
            </div>
          </header>
          <div>
            {userDoesNotHaveClasses && (
              <div className="no-classes">
                <p className="no-class-description">{NO_CLASSES_MESSAGE}</p>
                <p>
                  {NO_CLASSES_TRAINING_LINK}{' '}
                  <TextLink href="/dashboard">My Programs</TextLink>.
                </p>
              </div>
            )}
            {userHasClasses && (
              <ClassList
                classes={classes?.filter(c => c?.tier !== 2)}
                courseLinks={courseLinks}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                program={program}
                tier={1}
              />
            )}
          </div>
          <ButtonsContainer>
            <StyledButton onClick={handleCreateClass}>
              <StyledCreateClassIcon />
              {'Create a Class'}
            </StyledButton>
            <StyledButton
              onClick={() =>
                onComponentClick(
                  '/product/' + program + '/trainings',
                  NavigationHelper.types.PUSH,
                )
              }
            >
              <StyledAwardIcon />
              {'Training Articles'}
            </StyledButton>
          </ButtonsContainer>
        </section>
        {/* Tier 2 Small Groups Section */}
        <section id="small-groups">
          <header>
            <div className="header-text">
              <div className="headline">
                <HeaderThree>Small Groups</HeaderThree>
                <TierLabel labelText="TIER 2" />
                <div className="spacer" />
              </div>
              <p>Teach Tier 2 lessons to small groups and track progress</p>
            </div>
          </header>
          <div>
            {classes?.some(c => c?.tier === 2) && (
              <ClassList
                classes={classes?.filter(c => c?.tier === 2)}
                courseLinks={courseLinks}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                program={program}
                tier={2}
              />
            )}
          </div>
          <ButtonsContainer>
            <StyledButton onClick={handleCreateSmallGroup}>
              <StyledCreateClassIcon aria-hidden />
              {'Create a Small Group'}
            </StyledButton>
            <StyledButton
              onClick={() =>
                onComponentClick(
                  '/product/' + program + '/intervention-resources',
                  NavigationHelper.types.PUSH,
                )
              }
            >
              <StyledAwardIcon />
              {'Intervention Resources'}
            </StyledButton>
          </ButtonsContainer>
        </section>
        <section id="explore-the-program">
          <header>
            <div>
              <HeaderThree>{EXPLORE_PROGRAM_TITLE}</HeaderThree>
              <p>{EXPLORE_PROGRAM_DESCRIPTION}</p>
            </div>
          </header>

          <HeaderThree>Tier 1 Lessons</HeaderThree>
          <PreviewCourseList
            courseLinks={courseLinks?.filter(c => c.shouldShowOnLessonsPage)}
            onCourseClick={onCourseClick}
            program={program}
            tier={1}
          />
          <HeaderThree>Tier 2 Lessons</HeaderThree>
          <PreviewCourseList
            courseLinks={courseLinks?.filter(c => !c.shouldShowOnLessonsPage)}
            onComponentClick={onComponentClick}
            onCourseClick={onCourseClick}
            program={program}
            tier={2}
          />
        </section>
      </Wrapper>
    )
  },
)

ProductLessons.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoId: PropTypes.number,
      title: PropTypes.string,
      totalLessons: PropTypes.number,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  onCreateClass: PropTypes.func,
  onCreateSmallGroup: PropTypes.func,
  program: PropTypes.string,
  title: PropTypes.string.isRequired,
}
export default ProductLessons
